import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentCompany } from "@/hooks/useCurrentCompany";

export interface StorageLocationRow {
  id: string;
  company_id: string;
  name: string;
  aisles: number;
  bays_per_aisle: number;
  levels_per_bay: number;
  shelves_per_level: number;
  bins_per_shelf: number;
}

export interface StorageBinRow {
  id: string;
  location_id: string;
  address: string;
  aisle: number;
  bay: number;
  level: number;
  shelf: number;
  bin: number;
  description?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}


export function formatBinAddress(a: number, b: number, l: number, s: number, i: number): string {
  return `A${a.toString().padStart(2, "0")}-B${b}-L${l}-S${s}-B${i.toString().padStart(2, "0")}`;
}

export function useStorageLocations() {
  const { id: companyId } = useCurrentCompany();
  return useQuery({
    queryKey: ["storage_locations", companyId],
    enabled: !!companyId,
    queryFn: async (): Promise<StorageLocationRow[]> => {
      const { data, error } = await supabase
        .from("storage_locations")
        .select("*")
        .eq("company_id", companyId!)
        .order("created_at");
      if (error) throw error;
      return (data || []) as StorageLocationRow[];
    },
  });
}

export function useStorageBins(locationId?: string) {
  return useQuery({
    queryKey: ["storage_bins", locationId],
    enabled: !!locationId,
    queryFn: async (): Promise<StorageBinRow[]> => {
      const { data, error } = await supabase
        .from("storage_bins")
        .select("*")
        .eq("location_id", locationId!)
        .order("aisle")
        .order("bay")
        .order("level")
        .order("shelf")
        .order("bin");
      if (error) throw error;
      return (data || []) as StorageBinRow[];
    },
  });
}

export function useAllStorageBins() {
  const { id: companyId } = useCurrentCompany();
  return useQuery({
    queryKey: ["storage_bins_all", companyId],
    enabled: !!companyId,
    queryFn: async (): Promise<StorageBinRow[]> => {
      const { data, error } = await supabase
        .from("storage_bins")
        .select("*, storage_locations!inner(company_id)")
        .eq("storage_locations.company_id", companyId!)
        .order("address");
      if (error) throw error;
      return (data || []) as unknown as StorageBinRow[];
    },
  });
}

export function useUpdateBinDescription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, description }: { id: string; description: string }) => {
      const value = description.trim();
      const { error } = await supabase
        .from("storage_bins")
        .update({ description: value === "" ? null : value })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["storage_bins"] });
      queryClient.invalidateQueries({ queryKey: ["storage_bins_all"] });
    },
  });
}

